import React, { useEffect, useMemo, useState } from 'react';
import { Select, Spin, Typography } from 'antd';
import { surveyService } from '../../../services/survey.service';
import type { Survey } from '../../../types/survey.types';
import { previewRecipients } from '../../../services/bulk-email.service';

const { Text } = Typography;

interface SurveyPickerProps {
  onChange: (surveyId: number | null, recipientCount: number | null) => void;
}

/**
 * Seleção de pesquisa finalizada (US2, FR-004) — só pesquisas com status
 * COMPLETED aparecem como opção (mesma convenção de status da listagem
 * administrativa de pesquisas, Surveys.tsx). A contagem de destinatários é
 * recalculada via POST /admin/bulk-email/preview ao selecionar uma pesquisa.
 */
export const SurveyPicker: React.FC<SurveyPickerProps> = ({ onChange }) => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loadingSurveys, setLoadingSurveys] = useState(true);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);

  useEffect(() => {
    surveyService
      .listSurveys({ status: 'COMPLETED' as any, limit: 100 })
      .then((result) => setSurveys(result.data))
      .catch(() => setSurveys([]))
      .finally(() => setLoadingSurveys(false));
  }, []);

  const options = useMemo(
    () => surveys.map((survey) => ({ value: Number(survey.id), label: survey.name })),
    [surveys]
  );

  const handleSelect = (surveyId: number) => {
    setSelectedSurveyId(surveyId);
    setLoadingCount(true);
    previewRecipients({ source: 'SURVEY', surveyId })
      .then((result) => {
        setRecipientCount(result.recipientCount);
        onChange(surveyId, result.recipientCount);
      })
      .catch(() => {
        setRecipientCount(null);
        onChange(surveyId, null);
      })
      .finally(() => setLoadingCount(false));
  };

  return (
    <div>
      <Select
        showSearch
        style={{ width: '100%' }}
        placeholder="Selecione uma pesquisa finalizada"
        loading={loadingSurveys}
        value={selectedSurveyId ?? undefined}
        onChange={handleSelect}
        options={options}
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
        notFoundContent={loadingSurveys ? <Spin size="small" /> : 'Nenhuma pesquisa finalizada encontrada'}
      />
      {selectedSurveyId && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {loadingCount ? (
            <Spin size="small" />
          ) : recipientCount !== null ? (
            `${recipientCount} destinatário(s) encontrado(s) entre os participantes cadastrados desta pesquisa.`
          ) : (
            'Não foi possível calcular a contagem de destinatários.'
          )}
        </Text>
      )}
    </div>
  );
};
